/**
 * useReducedMotion — tracks the user's `prefers-reduced-motion` OS setting.
 *
 * Returns `true` when the user has asked the system to minimize animation.
 * Use the return value to toggle Recharts' `isAnimationActive` prop (on
 * <Bar>, <Line>, <Area>, <Pie>) so chart enter/update animations don't
 * play for users who requested them off. Listens to the media query so
 * the value updates live if the user flips the setting while the app is
 * open.
 */
import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(QUERY);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    // Safari < 14 uses addListener; modern browsers use addEventListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    } else {
      // @ts-ignore — legacy API
      mql.addListener(handler);
      // @ts-ignore
      return () => mql.removeListener(handler);
    }
  }, []);

  return reduced;
}

export default useReducedMotion;
